import type { ReactNode } from 'react';
import { Check } from 'lucide-react';

export interface LargeOption {
  value: string;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  disabled?: boolean;
}

/**
 * Card-grid single-select (replaces the legacy MUI LargeOptionPicker). Used to
 * choose an OS image or a resource flavor in the instance-create wizard.
 */
export function LargeOptionPicker({
  options,
  value,
  onChange,
  columns = 3,
}: {
  options: LargeOption[];
  value: string;
  onChange: (value: string) => void;
  columns?: number;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(720 / columns)}px, 1fr))`,
        gap: 'var(--vn-space-3)',
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            style={{
              textAlign: 'left',
              cursor: o.disabled ? 'not-allowed' : 'pointer',
              opacity: o.disabled ? 0.5 : 1,
              font: 'inherit',
              color: 'var(--vn-fg-default)',
              background: active ? 'var(--vn-accent-subtle, var(--vn-bg-hover))' : 'var(--vn-bg-surface)',
              border: `1.5px solid ${active ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
              borderRadius: 'var(--vn-radius-lg)',
              padding: 'var(--vn-space-3)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 'var(--vn-space-2)',
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600 }}>{o.title}</span>
              {o.subtitle ? (
                <span style={{ display: 'block', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', marginTop: 2 }}>
                  {o.subtitle}
                </span>
              ) : null}
              {o.meta ? (
                <span style={{ display: 'block', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', marginTop: 4 }}>
                  {o.meta}
                </span>
              ) : null}
            </span>
            {active ? (
              <span style={{ color: 'var(--vn-accent-solid)', flexShrink: 0 }}>
                <Check size={18} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
